import { useEffect, useRef } from 'react';
import type { SortItem } from '@/core/types';
import { Colors, getStatusColor } from '@/styles/colors';

interface Props {
  stateItems: SortItem[];
  stageRef: React.RefObject<HTMLDivElement | null>;
}

export function BarRenderer({ stateItems, stageRef }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const parent = stageRef.current;
    if (!canvas || !parent) return;

    const dpr = window.devicePixelRatio || 1;
    const w = parent.clientWidth;
    const h = parent.clientHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);

    const n = stateItems.length;
    const gap = n > 60 ? 1 : 3;
    const barW = Math.max(4, (w - gap * (n - 1)) / n);
    const padTop = 16;
    const padBottom = 28;
    const availH = h - padTop - padBottom;
    const maxVal = Math.max(...stateItems.map(i => i.value), 1);

    ctx.clearRect(0, 0, w, h);

    // background grid line
    ctx.strokeStyle = Colors.light.border;
    ctx.lineWidth = 0.5;
    for (let pct = 0.25; pct <= 1; pct += 0.25) {
      const gy = h - padBottom - availH * pct;
      ctx.beginPath();
      ctx.moveTo(0, gy);
      ctx.lineTo(w, gy);
      ctx.stroke();
      ctx.fillStyle = Colors.light.textSecondary;
      ctx.font = '9px monospace';
      ctx.textAlign = 'left';
      ctx.fillText(String(Math.round(maxVal * pct)), 4, gy - 3);
    }

    for (let idx = 0; idx < n; idx++) {
      const item = stateItems[idx];
      const barH = (item.value / maxVal) * availH;
      const x = idx * (barW + gap);
      const y = h - padBottom - barH;
      const color = getStatusColor((item as any)._status || 'idle');
      const r = Math.min(6, barW / 3);

      // bar shadow
      ctx.fillStyle = 'rgba(0,0,0,0.4)';
      ctx.beginPath();
      ctx.roundRect(x + 1, y + 1, barW, barH, r);
      ctx.fill();

      // bar fill
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.roundRect(x, y, barW, barH, r);
      ctx.fill();

      // value label
      if (barH > 20) {
        ctx.fillStyle = '#ffffff';
        ctx.font = `bold ${Math.min(11, barW * 0.5)}px monospace`;
        ctx.textAlign = 'center';
        ctx.fillText(String(item.value), x + barW / 2, y + barH - 5);
      }
    }
  });

  return <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />;
}
